import { postJsMessage } from '../../bridge';
import API from '../../api';
import { PostMessageOption } from '../../types';
import { ERROR_CODE, genError, isAvailable } from '../utils';
import { NativeAPIResponseMessage } from '@zoom/hybrid-jsbridge';
import type { ScreenshotOption, ScreenshotData } from './types';

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * common.openLinkInBrowser
 *
 * [PLATFORM]
 * mac        5.17.0+
 * windows    5.17.0+
 * linux      5.17.0+
 * ios        5.17.0+
 * android    5.17.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function openLinkInBrowser(
  url: string,
  options?: PostMessageOption
): Promise<any> {
  return isAvailable('common.openLinkInBrowser').then(() => {
    if (url) {
      return postJsMessage(
        API.openLinkInBrowser,
        { url },
        { ...options, timeout: 1, needResponse: false }
      );
    } else {
      return Promise.reject(
        genError({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.openLinkInBrowser,
          message: `url can't be empty!`,
        })
      );
    }
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * common.log
 *
 * [PLATFORM]
 * mac        6.4.0+
 * windows    6.4.0+
 * linux      6.4.0+
 * ios        6.4.0+
 * android    6.4.0+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/

export function log(
  message: string,
  logLevel: LogLevel = LogLevel.INFO,
  options?: PostMessageOption
): Promise<any> {
  return isAvailable('common.log').then(() => {
    if (message) {
      return postJsMessage(API.log, { message, logLevel }, options);
    } else {
      return Promise.reject(
        genError({
          errorCode: ERROR_CODE.REQUEST_PARAM_ERROR,
          apiName: API.log,
          message: `log message and logType can't be empty!`,
        })
      );
    }
  });
}
export enum LogLevel {
  DBG = 0,
  INFO = 1,
  WARNING = 2,
  ERROR = 3,
  ERROR_REPORT = 4,
  FATAL = 5,
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * common.screenshot
 *
 * [PLATFORM]
 * mac        6.2.0+
 * windows    6.2.0+
 * linux      6.2.0+
 * ios        notSupport
 * android    notSupport
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
let inScreenshot = false;
export async function screenshot(
  param: ScreenshotOption = { isHideZoomWindowInScreenshot: false }
): Promise<NativeAPIResponseMessage<ScreenshotData>> {
  await isAvailable('common.screenshot');
  if (inScreenshot) {
    return Promise.reject(
      genError({
        errorCode: ERROR_CODE.ALREADY_IN_SCREENSHOT_PROGRESS,
        apiName: API.screenshot,
        message:
          'screenshot is not allowed while a previous screenshot is still in progress.',
      })
    );
  } else {
    inScreenshot = true;
    return postJsMessage<ScreenshotOption, ScreenshotData>(
      API.screenshot,
      param,
      { timeout: 0 }
    ).finally(() => {
      inScreenshot = false;
    });
  }
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * common.appReady
 *
 * [PLATFORM]
 * mac        NotSupport
 * windows    NotSupport
 * linux      NotSupport
 * ios        6.3.10+
 * android    6.3.10+
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export function appReady(): Promise<any> {
  return isAvailable('common.appReady').then(() => {
    return postJsMessage(API.appReady, {}, { needResponse: false });
  });
}

/** * -------------------------------------------------- API Doc --------------------------------------------------
 * [API]
 * common.isDogfoodBuild
 *
 * [PLATFORM]
 * mac        6.4.5
 * windows    6.4.5
 * linux      6.4.5
 * ios        6.4.5
 * android    6.4.5
 *
 * -------------------------------------------------- API Doc --------------------------------------------------*/
export async function isDogfoodBuild(): Promise<
  NativeAPIResponseMessage<{ isDogfoodBuild: boolean }>
> {
  return isAvailable('common.isDogfoodBuild').then(() => {
    return postJsMessage(API.isDogfoodBuild, {});
  });
}

export default {
  isDogfoodBuild,
  openLinkInBrowser,
  log,
  screenshot,
  appReady,
};
